import { FC } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { Divider, Flex, VStack } from '@chakra-ui/react';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';

import * as Form from 'components/_core/form';
import OBStickyButton from 'features/Onboarding/components/OBStickyButton/OBStickyButton';

import zodLocales from '../../../../../../../common/zod/locales';
import {
  incomeRangeChoices,
  personalAssetsChoices,
} from '../complianceProperties';
import locales from '../locales';

const fiscalSituationSchema = z.object({
  incomeRange: z.string().min(1, zodLocales.errors.required),
  isSpecifiedUSPerson: z.boolean({
    invalid_type_error: zodLocales.errors.required,
    required_error: zodLocales.errors.required,
  }),
  personalAssets: z.string().min(1, zodLocales.errors.required),
});

export type FiscalSituationSchema = z.infer<typeof fiscalSituationSchema>;

interface ComplianceFormProps {
  initialValues: Partial<FiscalSituationSchema>;
  isSubmitting: boolean;
  onSubmit: (values: FiscalSituationSchema) => void;
}

/**
 * A form that asks for the user’s compliance info
 */
const ComplianceForm: FC<ComplianceFormProps> = ({
  initialValues,
  isSubmitting,
  onSubmit,
}) => {
  const {
    control,
    formState: { errors },
    handleSubmit,
  } = useForm<FiscalSituationSchema>({
    defaultValues: initialValues,
    mode: 'onChange',
    resolver: zodResolver(fiscalSituationSchema),
  });

  const submit = handleSubmit((values) => {
    onSubmit(values as FiscalSituationSchema);
  });

  return (
    <Flex as="form" direction="column" height="full" onSubmit={submit}>
      <VStack align="left" spacing="space-24">
        <Form.Field
          error={errors.personalAssets?.message}
          id="personalAssets"
          label={locales.assetsLabel}
        >
          <Controller
            control={control}
            name="personalAssets"
            render={({ field }) => (
              <Form.SunshineSelect
                isDisabled={isSubmitting}
                onChange={(option) => field.onChange(option?.value)}
                options={personalAssetsChoices}
                value={personalAssetsChoices.find(
                  ({ value }) => value === field.value,
                )}
              />
            )}
          />
        </Form.Field>

        <Form.Field
          error={errors.incomeRange?.message}
          id="incomeRange"
          label={locales.incomeLabel}
        >
          <Controller
            control={control}
            name="incomeRange"
            render={({ field }) => (
              <Form.SunshineSelect
                isDisabled={isSubmitting}
                onChange={(option) => field.onChange(option?.value)}
                options={incomeRangeChoices}
                value={incomeRangeChoices.find(
                  ({ value }) => value === field.value,
                )}
              />
            )}
          />
        </Form.Field>

        <Divider />
        <Form.Field
          data-testid="isSpecifiedUSPerson"
          error={errors.isSpecifiedUSPerson?.message}
          label={locales.sectionLabelUsCitizen}
        >
          <Controller
            control={control}
            name="isSpecifiedUSPerson"
            render={({ field: { onChange, value } }) => (
              <Form.Radio.Group
                direction="row"
                isDisabled={isSubmitting}
                onChange={(val) => onChange(val === 'true')}
                radioPosition="right"
                value={value === undefined ? undefined : String(value)}
                variant="outline"
              >
                <Form.Radio value="true">
                  <Form.Radio.Title>{locales.yes}</Form.Radio.Title>
                </Form.Radio>

                <Form.Radio value="false">
                  <Form.Radio.Title>{locales.no}</Form.Radio.Title>
                </Form.Radio>
              </Form.Radio.Group>
            )}
          />
        </Form.Field>
      </VStack>

      <OBStickyButton
        isLoading={isSubmitting}
        justifyContent="left"
        type="submit"
      >
        {locales.nextButtonLabel}
      </OBStickyButton>
    </Flex>
  );
};

export default ComplianceForm;
